import { useState } from 'react';
import { Can } from '@/components/common/Can';
import type { PermissionCode } from '@/constants/permissions';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { showApiErrorToast } from '@/utils/errors';

interface ExportButtonProps {
  onExport: () => Promise<void>;
  disabled?: boolean;
  label?: string;
  /**
   * The code the export needs. Exports are their own permission in the
   * catalogue, separate from reading the screen, so the button goes when the
   * caller holds only the read.
   */
  requires?: PermissionCode | undefined;
}

export function ExportButton({ onExport, disabled, label = 'Excel', requires }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  async function handleClick() {
    setIsExporting(true);
    try {
      await onExport();
      toast.success('Export downloaded');
    } catch (error) {
      showApiErrorToast(error, 'Failed to export');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Can code={requires}>
      <Button
        type="button"
        variant="outline"
        className="gap-2"
        onClick={() => void handleClick()}
        disabled={disabled || isExporting}
      >
        {isExporting ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Download className="size-4" />
        )}
        {label}
      </Button>
    </Can>
  );
}
